import { useState } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ROUTES } from "./routerConfig";
import { recordVisitedPath } from "../utils/routeHistory";
import NotFoundLayout from "../components/layouts/NotFoundLayout";
import NotFound from "../pages/other/NotFound";
import SessionTimeout from "../pages/other/SessionTimeout";
import NotificationListing from "../pages/NotificationListing/NotificationListing";
import LoginType from "../pages/auth/LoginType";
import FlexiTermLoan from "../components/common/FlexiTermLoan";
import MainDashboard from "../pages/dashboard/flexiTermLoan/MainDashboard";
import RequestForDisbursement from "../pages/dashboard/termLoan/RequestForDisbursementTerm";
import ViewAllLoans from "../pages/dashboard/termLoan/ViewAllLoans";
import Register from "../pages/auth/Register";
import ThankYouPage from "../pages/auth/ThankYouPage";
import AccountSummary from "../pages/dashboard/flexiTermLoan/AccountSummary";
import UnAuthRoutes from "./UnAuthRoutes";
import AuthRoutes from "./AuthRoutes";
import QrcRoutes from "./QrcRoutes";
import Otp from "../pages/auth/Otp";
import SelectProfile from "../pages/auth/SelectProfile";
import SetPassword from "../pages/auth/SetPassword";
import EnterPassword from "../pages/auth/EnterPassword";
import ForgotPassword from "../pages/auth/ForgotPassword";
import Login from "../pages/auth/Login";
import PasswordCreated from "../pages/auth/PasswordCreated";
import DashboardLayout from "../components/layouts/DashboardLayout";
import AuthLayout from "../components/layouts/AuthLayout";
import UserProfile from "../pages/userprofile/UserProfile";
import ChangePassword from "../pages/userprofile/ChangePassword";
import DashboardLayoutJourny from "../components/layouts/DashboardLayoutJourny";
import DashboardJourny from "../pages/dashboard/Dashboard";
import HorizontalLinearStepper from "../pages/common-features/HorizontalLinearStepper";
import MandatoryDocumentsView from "../pages/summary/MandatoryDocumentsView";
import ViewBulkDocumentsView from "../pages/summary/ViewBulkDocumentsView";
import ViewAllApplications from "../pages/dashboard/ViewAllApplications";
import Summary from "../pages/summary/Summary";
import ViewSummary from "../pages/summary/ViewSummary";
import LoanApplicationOverview from "../pages/summary/LoanApplicationOverview";
import DocumentCollectionOtp from "../pages/triggerLinkToCollectDocuments/DocumentCollectionOtp";
import DocumentUploadPage from "../pages/triggerLinkToCollectDocuments/DocumentUploadPage";
import NewApplication from "../pages/dashboard/NewApplication";
import ExistingApplication from "../pages/dashboard/ExistingApplication";
import PreLoginJourney from "../pages/prelogin-journey";
import ContactUsHub from "../pages/support/ContactUsHub";
import RaiseARequest from "../pages/support/RaiseARequest";
import ViewTickets from "../pages/support/ViewTickets";
import TicketDetails from "../pages/support/TicketDetails";
import LodgeAComplaint from "../pages/support/LodgeAComplaint";
import HelpCenter from "../pages/helpCenter/HelpCenter";
import ConsentAndAuthorizationPage from "../pages/consent-authorization";

// Records each route change so screens can tell where the user came from (see utils/routeHistory).
// Recorded during render, not in an effect: this component renders before <Routes/>, so the page
// being navigated to can read an accurate "previous path" in its own first render. From an effect
// it would run too late — the page would still see itself as the previous path. recordVisitedPath
// ignores a repeat of the same path, so StrictMode's double render is harmless.
const RouteHistoryTracker = () => {
  const location = useLocation();
  recordVisitedPath(location.pathname);
  return null;
};

const Router = () => {
  const [steps, setSteps] = useState("");
  // Carries the just-entered password from EnterPassword to LoginType for a
  // multi-business-type login (LoginType needs it to complete the login call
  // for whichever business type gets picked) — kept here, in this component's
  // own local state, instead of Redux, so it's never in the globally
  // inspectable store (visible via Redux DevTools) even for that short window.
  const [pendingLoginPassword, setPendingLoginPassword] = useState("");
  // const isServerUp = process.env.REACT_APP_SERVER_STATUS === "false";
  // useEffect(() => {
  //   localStorage.clear();
  // }, []);

  const renderStepsComponent = () => {
    switch (steps) {
      case ROUTES?.Login?.otp?.path:
        return <Otp setNextStep={setSteps} />;
      case ROUTES?.Login?.register?.path:
        return <Register setNextStep={setSteps} />;
      case ROUTES?.Login?.selectProfile?.path:
        return <SelectProfile setNextStep={setSteps} />;
      case ROUTES?.Login?.thankYouPage?.path:
        return <ThankYouPage setNextStep={setSteps} />;
      case ROUTES?.Login?.setPassword?.path:
        return <SetPassword setNextStep={setSteps} />;
      case ROUTES?.Login?.enterPassword?.path:
        return (
          <EnterPassword
            setNextStep={setSteps}
            setPendingLoginPassword={setPendingLoginPassword}
          />
        );
      case ROUTES?.Login?.forgotPassword?.path:
        return <ForgotPassword setNextStep={setSteps} />;
      case ROUTES?.Login?.passwordCreated?.path:
        return <PasswordCreated setNextStep={setSteps} />;
      case ROUTES?.Login?.loginType?.path:
        return (
          <LoginType
            setNextStep={setSteps}
            pendingLoginPassword={pendingLoginPassword}
            setPendingLoginPassword={setPendingLoginPassword}
          />
        );
      default:
        return <Login setNextStep={(value: string) => setSteps(value)} />;
    }
  };

  const renderApplyNowComponent = () => {
    return <DashboardLayoutJourny />;
  };

  return (
    <>
    <RouteHistoryTracker />
    <Routes>
      {/* Base Routes */}
      <Route path={ROUTES.Base.index}>
        <Route
          index
          element={<Navigate to={ROUTES.Base.indexReplacePath} replace />}
        />
      </Route>

      {/* Fully public route — accessible regardless of auth state */}
      <Route path={ROUTES.HelpCenter.index} element={<HelpCenter />} />

      <Route path={ROUTES.ApplyNow.index} element={<UnAuthRoutes />}>
        <Route index element={renderApplyNowComponent()} />
      </Route>
      <Route path={ROUTES.ApplyLoan.index} element={<UnAuthRoutes />}>
        <Route index element={<PreLoginJourney />} />
      </Route>
      <Route path={ROUTES.ConsentAuthorization.index} element={<UnAuthRoutes />}>
        <Route index element={<ConsentAndAuthorizationPage />} />
      </Route>

      {/* Public Routes */}
      <Route element={<UnAuthRoutes />}>
        <Route path={ROUTES.Login.index} element={<AuthLayout />}>
          <Route index element={renderStepsComponent()} />
        </Route>

        <Route path={ROUTES.DocumentCollection.index} element={<AuthLayout />}>
          <Route
            path={ROUTES.DocumentCollection.otp.path}
            element={<DocumentCollectionOtp />}
          />
        </Route>
      </Route>

      <Route element={<AuthRoutes />}>
        <Route
          path={ROUTES.Application.index}
          element={<DashboardLayoutJourny />}
        >
          {/* <Route index element={<DashboardJourny />} /> */}
          <Route index element={<NewApplication />} />
          <Route
            path={ROUTES.Application.existingApplication.path}
            element={<ExistingApplication />}
          />
          <Route
            path={ROUTES.Application.uploadDocument.path}
            element={<Summary />}
          />
          <Route
            path={ROUTES.Application.summary.path}
            element={<ViewSummary />}
          />
          <Route
            path={ROUTES.Application.loanSummary.path}
            element={<LoanApplicationOverview />}
          />
          <Route
            path={ROUTES.Application.applyNow.path}
            element={<HorizontalLinearStepper status={true} />}
          />
          <Route
            path={ROUTES.Application.applyNow.path}
            element={<HorizontalLinearStepper status={true} />}
          />
          <Route
            path={`${ROUTES.Application.viewAllApplications.path}`}
            element={<ViewAllApplications />}
          />
          <Route
            path={`${ROUTES.Application.mandatoryDocuments.path}`}
            element={<MandatoryDocumentsView />}
          />
          <Route
            path={`${ROUTES?.Application?.bulkDocuments?.path}`}
            element={<ViewBulkDocumentsView />}
          />
        </Route>

        <Route
          path={ROUTES.DocumentCollection.index}
          // element={<DashboardLayoutJourny />}
        >
          <Route
            path={ROUTES.DocumentCollection.uploadDocs.path}
            element={<DocumentUploadPage />}
          />
        </Route>

        <Route path={ROUTES.Profile.index} element={<DashboardLayoutJourny />}>
          <Route index element={<UserProfile />} />
          <Route
            path={ROUTES.Profile.changePassword.path}
            element={<ChangePassword />}
          />
        </Route>

        <Route
          path={ROUTES.Dashboard.index}
          element={<DashboardLayoutJourny />}
        >
          <Route index element={<MainDashboard />} />

          <Route
            path={ROUTES.Dashboard.flexiTermLoan.path}
            element={<FlexiTermLoan />}
          />
          <Route
            path={ROUTES.Dashboard.termLoanPage.path}
            element={<FlexiTermLoan />}
          />
          <Route
            path={ROUTES.Dashboard.flexiTermLoan.accountSummary.path}
            element={<AccountSummary />}
          />
          <Route
            path={ROUTES.Dashboard.termLoanPage.requestForDisbursement.path}
            element={<RequestForDisbursement />}
          />
          {/* <Route
            path={`${ROUTES.Dashboard.termLoanPage.path}/${ROUTES.Dashboard.termLoanPage.viewAllLoans.path}`}
            element={<ViewAllLoans />}
          /> */}
          <Route
            path={`${ROUTES.Dashboard.termLoanPage.path}/${ROUTES.Dashboard.termLoanPage.disburseMentData.path}`}
            element={<RequestForDisbursement />}
          />
        </Route>
        <Route
          path={ROUTES.Dashboard.index}
          element={<DashboardLayout />}
        >
           <Route
            path={`${ROUTES.Dashboard.termLoanPage.path}/${ROUTES.Dashboard.termLoanPage.viewAllLoans.path}`}
            element={<ViewAllLoans />}
          />
        </Route>

        <Route path={ROUTES.Notifications.index} element={<DashboardLayout />}>
          <Route index element={<NotificationListing />} />
        </Route>

      </Route>

      {/* Reachable by a normal logged-in session OR a pre-login QRC-verified session
          (see HelpCenter.tsx + QrcRoutes) */}
      <Route element={<QrcRoutes />}>
        <Route path={ROUTES.ContactUs.index} element={<DashboardLayoutJourny />}>
          <Route element={<ContactUsHub />}>
            <Route
              path={ROUTES.ContactUs.raiseRequest.path}
              element={<RaiseARequest />}
            />
            <Route
              path={ROUTES.ContactUs.viewTickets.path}
              element={<ViewTickets />}
            />
            <Route
              path={ROUTES.ContactUs.ticketDetails.path}
              element={<TicketDetails />}
            />
          </Route>
          <Route
            path={ROUTES.ContactUs.lodgeComplaint.path}
            element={<LodgeAComplaint />}
          />
        </Route>
      </Route>

      {/* Private Routes */}
      {/* <Route element={<AuthRoutes />}>
        <Route path={ROUTES.Dashboard.index} element={<DashboardLayout />}>
          <Route index element={<MainDashboard />} />
          <Route
            path={ROUTES.Dashboard.flexiTermLoan.path}
            element={<FlexiTermLoan />}
          />
          <Route
            path={ROUTES.Dashboard.termLoanPage.path}
            element={<FlexiTermLoan />}
          />
          <Route
            path={ROUTES.Dashboard.flexiTermLoan.accountSummary.path}
            element={<AccountSummary />}
          />
          <Route
            path={ROUTES.Dashboard.termLoanPage.requestForDisbursement.path}
            element={<RequestForDisbursement />}
          />
          <Route
            path={`${ROUTES.Dashboard.termLoanPage.path}/${ROUTES.Dashboard.termLoanPage.viewAllLoans.path}`}
            element={<ViewAllLoans />}
          />
          <Route
            path={`${ROUTES.Dashboard.termLoanPage.path}/${ROUTES.Dashboard.termLoanPage.disburseMentData.path}`}
            element={<RequestForDisbursement />}
          />
        </Route>
        <Route path={ROUTES.Profile.index} element={<DashboardLayout />}>
          <Route index element={<UserProfile />} />
          <Route
            path={ROUTES.Profile.changePassword.path}
            element={<ChangePassword />}
          />
        </Route>
        <Route path={ROUTES.Notifications.index} element={<DashboardLayout />}>
          <Route index element={<NotificationListing />} />
        </Route>
      </Route> */}

      {/* Page not found */}
      <Route path={ROUTES.Base.notFound} element={<NotFoundLayout />}>
        <Route index element={<NotFound />} />
      </Route>
      <Route path={ROUTES.Base.sessionTimeout} element={<NotFoundLayout />}>
        <Route index element={<SessionTimeout />} />
      </Route>
      <Route
        path={ROUTES.Base.notMatch}
        element={<Navigate to={ROUTES.Base.notFound} />}
      />
    </Routes>
    </>
  );
};

export default Router;
